import React, { useEffect, useState } from "react";
import {
  Center,
  Container,
  Table,
  Text,
  Title,
  Button,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
} from "@mantine/core";
import { getProductType } from "../api";
import { Link } from "wouter";
import { useProductTypesStore } from "./store";
import AttributeDialog from "./AttributeDialog";
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconTrash,
  IconAdjustments,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import AttributeOverrideDialog from "./AttributeOverrideDialog";
import { useStore } from "../store";

export default function Details({ params }) {
  const [productType, setProductType] = useState(null);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [overrideOpened, setOverrideOpened] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const profile = useStore((state) => state.profile);

  const {
    attributes,
    setCurrentProductType,
    deleteAttribute,
    loading: storeLoading,
  } = useProductTypesStore();

  const leafTypeCode = productType?.hierarchy?.levels?.length
    ? productType.hierarchy.levels[productType.hierarchy.levels.length - 1]
        ?.code
    : undefined;

  useEffect(() => {
    getProductType(params.marketplaceId, params.id).then((data) => {
      setProductType(data);
      // Set the product type in store, which will automatically extract attributes
      setCurrentProductType(data);
    });
  }, [params.marketplaceId, params.id, setCurrentProductType]);

  const handleCreateAttribute = () => {
    setSelectedAttribute(null);
    setDialogOpened(true);
  };

  const handleEditAttribute = (attribute) => {
    setSelectedAttribute(attribute);
    setDialogOpened(true);
  };

  const handleViewAttribute = (attribute) => {
    setSelectedAttribute(attribute);
    setDialogOpened(true);
  };

  const getOriginBadge = (storeAttribute) => {
    if (!storeAttribute) return null;
    if (storeAttribute.origin === "manualOverride") {
      return (
        <Badge color="yellow" variant="light" size="xs">
          Override
        </Badge>
      );
    }
    if (
      (storeAttribute.descriptionOverride ||
        storeAttribute.exampleOverride ||
        storeAttribute.validationOverride) &&
      profile?.role === "GlobalAdmin"
    ) {
      return (
        <Badge color="grape" variant="light" size="xs">
          Overridden
        </Badge>
      );
    }
    return null;
  };

  const canEdit = (attribute) => {
    return attribute.origin === "manualOverride";
  };

  const openOverride = (attribute) => {
    setOverrideTarget(attribute);
    setOverrideOpened(true);
  };

  const openDelete = (attribute) => {
    setDeleteTarget(attribute);
    setDeleteOpened(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAttribute(params.marketplaceId, deleteTarget.id);
      // Refresh product type so rows reflect deletion (manual override removal)
      const refreshed = await getProductType(params.marketplaceId, params.id);
      setProductType(refreshed);
      notifications.show({
        title: "Deleted",
        message: `Attribute ${deleteTarget.code} deleted`,
        color: "green",
      });
    } catch (e) {
      notifications.show({
        title: "Error",
        message: e.message || "Failed to delete attribute",
        color: "red",
      });
    } finally {
      setDeleteOpened(false);
      setDeleteTarget(null);
    }
  };

  const keys = Object.keys(productType?.fieldValues ?? {});
  // Sort attributes by:
  // 1. Overrides (origin === 'manualOverride') first
  // 2. Specificity of declared hierarchy level (more specific / deeper level first)
  //    productType.hierarchy.levels: first = most generic, last = most specific
  // 3. Fallback alphabetical by code for stability
  const hierarchyLevels = productType?.hierarchy?.levels || [];
  const levelIndexByCode = new Map(
    hierarchyLevels.map((l, idx) => [l.code, idx])
  );

  const isOverride = (code) => {
    const storeAttr = attributes.find((a) => a.code === code);
    return storeAttr?.origin === "manualOverride";
  };

  const getLevelIndex = (code) => {
    const attr = productType.fieldValues[code];
    if (!attr) return -1; // unknown
    return levelIndexByCode.get(attr.hierarchyCode) ?? -1;
  };

  const sortedKeys = [...keys].sort((a, b) => {
    const aOverride = isOverride(a);
    const bOverride = isOverride(b);
    if (aOverride !== bOverride) return aOverride ? -1 : 1; // overrides first

    const aLevel = getLevelIndex(a);
    const bLevel = getLevelIndex(b);
    if (aLevel !== bLevel) return bLevel - aLevel; // deeper (higher index) first

    return a.localeCompare(b);
  });

  const rows = sortedKeys.map((key) => {
    // console.log("row key", key);

    // TBD: This doesn't include e.g. MEDIA at all, why are we not using hydratedType?
    const attr = productType.fieldValues[key];
    // const attr = productType.hydratedType.attributes[key];
    const attrDetails = productType.hydratedType.attributes.find(
      (p) => p.code === key
    );

    // Find the corresponding attribute from the store to get origin info
    const storeAttribute = attributes.find((a) => a.code === key);

    return (
      <React.Fragment key={key}>
        <Table.Tr>
          <Table.Td>
            <Badge size="xs" variant="default">
              {attr.hierarchyCode || "root"}
            </Badge>
          </Table.Td>
          <Table.Td
            style={{
              textWrap: "break-word",
              wordWrap: "break-word",
            }}
          >
            <Group gap="xs">
              {attr.code}
              {getOriginBadge(storeAttribute)}
            </Group>
            <Text fz="sm" c="dimmed">
              {attr.label}
            </Text>
          </Table.Td>
          <Table.Td
            style={{
              textWrap: "break-word",
              wordWrap: "break-word",
            }}
          >
            {(attr.type === "LIST" || attr.type === "LIST_MULTIPLE_VALUES") &&
            attrDetails?.listCode ? (
              <Link
                to={`/marketplaces/${attrDetails.destinationMarketplaceId}/lists/${attrDetails.listCode}`}
              >
                {attr.type}
              </Link>
            ) : (
              <Text fz="sm">{attr.type}</Text>
            )}
            <Text fz="sm" c="dimmed">
              {attr.requirementLevel}
            </Text>
            {attr.channelCodes ? (
              <div>Channels: {attr.channelCodes.join(", ")}</div>
            ) : null}
          </Table.Td>
          <Table.Td>
            <Text fz="sm" c="dimmed">
              {attrDetails?.roles && attrDetails.roles.length > 0
                ? "ROLE: " + attrDetails.roles[0].type
                : ""}
            </Text>
            <Text
              style={{
                textWrap: "break-word",
                wordWrap: "break-word",
              }}
              fz="sm"
              c="dimmed"
            >
              {attrDetails?.defaultValue
                ? "DEFAULT VALUE: " + attrDetails?.defaultValue
                : ""}
            </Text>
          </Table.Td>
          <Table.Td
            style={{
              textWrap: "break-word",
              wordWrap: "break-word",
              maxWidth: "300px",
            }}
          >
            {attrDetails?.validations &&
              attrDetails.validations.map((validation) => (
                <Text key={attrDetails.code + "v" + validation.type} fz="sm">
                  {validation.type === "forbiddenWords"
                    ? validation.type
                    : `${validation.type}: ${validation.parameter}`}
                </Text>
              ))}
            {storeAttribute?.validationOverride &&
              profile?.role === "GlobalAdmin" && (
                <Badge color="orange" size="xs" variant="light" mt="xs">
                  Validation Override ({storeAttribute.validationOverrideMode})
                </Badge>
              )}
          </Table.Td>
          {profile?.role === "GlobalAdmin" && (
            <Table.Td style={{ width: 0, whiteSpace: "nowrap" }}>
              <Group wrap="nowrap" gap={4}>
                {storeAttribute ? (
                  <>
                    <Tooltip label="Delete attribute">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => openDelete(storeAttribute)}
                      >
                        <IconTrash size="1rem" />
                      </ActionIcon>
                    </Tooltip>
                    {canEdit(storeAttribute) ? (
                      <Tooltip label="Edit attribute">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleEditAttribute(storeAttribute)}
                        >
                          <IconEdit size="1rem" />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip label="Edit overrides">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => openOverride(storeAttribute)}
                          >
                            <IconAdjustments size="1rem" />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="View attribute">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleViewAttribute(storeAttribute)}
                          >
                            <IconEye size="1rem" />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                  </>
                ) : null}
              </Group>
            </Table.Td>
          )}
        </Table.Tr>
        {(storeAttribute?.descriptionOverride || attr.description) && (
          <Table.Tr style={{ borderTop: "none" }} key={key + "desc"}>
            <Table.Td></Table.Td>
            <Table.Td colSpan={5}>
              <Group ml="lg" gap="xs" align="flex-start">
                <Text fz="xs" fs="italic" style={{ flex: 1 }}>
                  {storeAttribute?.descriptionOverride || attr.description}
                </Text>
                {storeAttribute?.descriptionOverride &&
                  profile?.role === "GlobalAdmin" && (
                    <Badge color="grape" size="xs" variant="light">
                      Override
                    </Badge>
                  )}
              </Group>
            </Table.Td>
          </Table.Tr>
        )}
        {storeAttribute?.validationOverride &&
          profile?.role === "GlobalAdmin" && (
            <Table.Tr style={{ borderTop: "none" }} key={key + "validation"}>
              <Table.Td></Table.Td>
              <Table.Td colSpan={5}>
                <Group ml="lg" gap="xs" align="flex-start">
                  <Text fz="xs" fs="italic" style={{ flex: 1 }}>
                    Validation Override: {storeAttribute.validationOverride}
                  </Text>
                  <Badge color="orange" size="xs" variant="light">
                    {storeAttribute.validationOverrideMode} Mode
                  </Badge>
                </Group>
              </Table.Td>
            </Table.Tr>
          )}
      </React.Fragment>
    );
  });

  if (!productType)
    return (
      <Container size="xl">
        <Center>Loading...</Center>
      </Container>
    );

  // Calculate override statistics
  const overrideStats = attributes.reduce(
    (stats, attr) => {
      if (attr.origin === "manualOverride") {
        stats.manualOverrides++;
      }
      if (attr.descriptionOverride) {
        stats.descriptionOverrides++;
      }
      if (attr.exampleOverride) {
        stats.exampleOverrides++;
      }
      if (attr.validationOverride) {
        stats.validationOverrides++;
      }
      return stats;
    },
    {
      manualOverrides: 0,
      descriptionOverrides: 0,
      exampleOverrides: 0,
      validationOverrides: 0,
    }
  );

  return (
    <Container size="xl">
      <Group justify="space-between" align="center" mb="xl">
        <div>
          <Title order={1}>{productType.typeNamePath}</Title>
          {profile?.role === "GlobalAdmin" && (
            <Group gap="xs" mt="xs">
              <Badge color="yellow" variant="light" size="sm">
                Manual Overrides: {overrideStats.manualOverrides}
              </Badge>
              <Badge color="grape" variant="light" size="sm">
                Description Overrides: {overrideStats.descriptionOverrides}
              </Badge>
              <Badge color="violet" variant="light" size="sm">
                Example Overrides: {overrideStats.exampleOverrides}
              </Badge>
              <Badge color="orange" variant="light" size="sm">
                Validation Overrides: {overrideStats.validationOverrides}
              </Badge>
            </Group>
          )}
        </div>
        <Button
          leftSection={<IconPlus size="1rem" />}
          onClick={handleCreateAttribute}
          loading={storeLoading}
        >
          Add Attribute
        </Button>
      </Group>
      <Table mt="xl" withTableBorder stickyHeader stickyHeaderOffset={60}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Hierarchy</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Validations</Table.Th>
            {profile?.role === "GlobalAdmin" && (
              <Table.Th
                style={{ width: 0, whiteSpace: "nowrap", paddingRight: 8 }}
              >
                Actions
              </Table.Th>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>

      <AttributeDialog
        opened={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          setSelectedAttribute(null);
        }}
        attribute={selectedAttribute}
        marketplaceId={params.marketplaceId}
        hierarchyCode={leafTypeCode}
      />

      <AttributeOverrideDialog
        opened={overrideOpened}
        onClose={() => {
          setOverrideOpened(false);
          setOverrideTarget(null);
        }}
        attribute={overrideTarget}
        marketplaceId={params.marketplaceId}
        onUpdated={async () => {
          const refreshed = await getProductType(
            params.marketplaceId,
            params.id
          );
          setProductType(refreshed);
        }}
      />

      <Modal
        opened={deleteOpened}
        onClose={() => {
          setDeleteOpened(false);
          setDeleteTarget(null);
        }}
        title="Delete Attribute"
        centered
      >
        <Text mb="sm">
          Are you sure you want to delete attribute{" "}
          <Text span fw={600}>
            {deleteTarget?.code}
          </Text>{" "}
          ? This action cannot be undone.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button
            variant="outline"
            onClick={() => {
              setDeleteOpened(false);
              setDeleteTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            loading={storeLoading}
          >
            Delete
          </Button>
        </Group>
      </Modal>

      {/* <div>
        ALL:
        <Code block>{JSON.stringify(productType, null, 2)}</Code>
      </div> */}
    </Container>
  );
}
